import axios, { AxiosError } from 'axios'
import { useState } from 'react'
import { useQuery } from 'react-query'
import { RequestInterface } from './RequestInterface'
import { ErrorTypeEnum, Message } from './ResponseErrorType'
import { toast } from 'react-toastify'

type UseFetchType<TData, TPayload, TQuery> = {
  request: RequestInterface<TPayload, TQuery> &
  (TData extends void
    ? 'باید تایپ تی رو بدی ندی من بهت گیر میدم :)'
    : RequestInterface<TPayload, TQuery>)
  onSuccess?: (data: TData) => void
  fetchInitialLoad?: boolean
  onStatusCode201?: (data: any) => void
  onStatusCode400?: (data: any) => void
  onError?: (data: any) => void
  order?: number // داخل کامپوینت ها وقتی از ی درخواست دوباره استفاده بشه این کامپوینت جفتشونو یکی حساب میکنه با این فیلد میتونیم بگیم کیا یکین یا نیستن
}

function useFetch<TData = void, TPayload = {}, TQuery = {}>({
  request,
  fetchInitialLoad,
  order,
  onSuccess,
  onStatusCode201,
  onStatusCode400,
  onError
}: UseFetchType<TData, TPayload, TQuery>): ResponseInterface<
  TData,
  TPayload,
  TQuery
> {
  const [query, setQuery] = useState<TQuery>()
  const [body, setBody] = useState<TPayload>()
  const [enable, setEnable] = useState<boolean>(fetchInitialLoad ?? false)
  if (!request.headers) request.headers = {}
  if (request.authToken)
    request.headers!.Authorization = `Bearer ${request.authToken}`
  const baseURL: string | undefined =
    request.domain ?? window.env['APP_Domain']
  const { data, isFetching, error, isSuccess } = useQuery<TData>(
    [request.url, request.method, request.domain, body, order],
    async () => {
      try {
        switch (request.method) {
          case 'POST': {
            const res = await axios.post<TData>(request.url, body, {
              baseURL,
              headers: request.headers,
              params: query,
            })
            return checkData(
              res.status,
              res.data,
              onStatusCode201,
              onStatusCode400,
              onError
            )
          }
          case 'GET': {
            const res = await axios.get<TData>(request.url, {
              baseURL,
              headers: request.headers,
              params: query,
            })
            return checkData(
              res.status,
              res.data,
              onStatusCode201,
              onStatusCode400,
              onError
            )
          }
          case 'DELETE': {
            const res = await axios.delete<TData>(request.url, {
              baseURL,
              headers: request.headers,
              params: query,
            })
            return checkData(
              res.status,
              res.data,
              onStatusCode201,
              onStatusCode400,
              onError
            )
          }
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          return checkData(
            error.response?.status,
            error.response?.data,
            onStatusCode201,
            onStatusCode400,
            onError
          )
        }
        throw error
      }
    },
    {
      retry: false,
      enabled: enable,
      onSettled(data, error) {
        setEnable(false)
      },
      onSuccess: onSuccess,
      staleTime: 0,
      cacheTime: 0,
    }
  )

  return {
    result: data,
    error: error,
    isLoading: isFetching,
    isSuccess: isSuccess,
    payLoad: body,
    query: query,
    reFetch(payload, query) {
      setBody(payload)
      setQuery(query)
      setEnable(true)
    },
  }
}

const checkData = (status, data, onStatusCode201, onStatusCode400, onError) => {
  if (status === 200) return data
  else if (status === 400) {
    const messages = data?.messages as Array<Message>
    onError?.(data)
    if (onStatusCode400) {
      onStatusCode400?.()
    }
    else {
      messages?.forEach((n) => {
        if (n.flag === ErrorTypeEnum.Error) toast.error(n.message, { autoClose: false })
        else if (n.flag === ErrorTypeEnum.Info) toast.info(n.message)
        else if (n.flag === ErrorTypeEnum.Warning) toast.warn(n.message, { autoClose: false })
        else if (n.flag === ErrorTypeEnum.Success) toast.success(n.message, { autoClose: false })
        else toast.info(n.message)
      })
    }
    throw new Error('fetch data faild !')
  } else if (status === 201) {
    onStatusCode201?.(data)
    throw new Error('fetch data faild !')
  } else {
    if (onError)
      onError?.(data)
    else {
      toast.error('خطایی در ارسال درخواست')
    }
    throw new Error('fetch data faild !')

  }
}
export default useFetch
